import { FC } from "react"
import { Stack } from "@mui/material"
import {
  AutocompleteElement,
  TextFieldElement,
  useFormContext,
} from "react-hook-form-mui"
import UrlButtons from "@/modules/postBot/components/posts/postField/urlButtons"
import { GuestBot } from "../../interfaces"
import {
  buttonsToInlineKeyboard,
  inlineKeyboardToButtons,
} from "./buttonsAdapter"
import { isUrl } from "./payloadForm"

/**
 * Редактор `ResponsePayload`. Рендерится внутри существующего RHF
 * `FormContainer` (через `useFormContext`).
 *
 * Обоснованные отклонения от ТЗ §6.3:
 *  1. Нет Quill-редактора. В `ResponsePayload` нет поля `entities` (только
 *     `text` + `parseMode`), поэтому вывод Quill (entities) некуда девать —
 *     используется обычный многострочный `TextFieldElement`.
 *  2. Свежий select форматирования. Существующий `parseModeSelectField.tsx`
 *     жёстко завязан на `name="body.parse_mode"` со сломанным value-map, поэтому
 *     здесь реализован отдельный `AutocompleteElement` с гостевыми значениями
 *     (`html`/`md`).
 *
 * Валидация делается на уровне полей (см. Task 9): бросать ошибку в submit-
 * хендлере нельзя — она будет проглочена RHF и не показана.
 */
const TYPE_OPTIONS = [
  { id: "text", label: "Текст" },
  { id: "photo", label: "Фото" },
  { id: "document", label: "Документ" },
]
const PARSE_MODE_OPTIONS = [
  { id: "html", label: "HTML" },
  { id: "md", label: "Markdown" },
]
const MIME_OPTIONS = [
  { id: "application/pdf", label: "PDF" },
  { id: "application/zip", label: "ZIP" },
]

export const PayloadEditor: FC = () => {
  const { watch, setValue } = useFormContext()
  const type = watch("type") as GuestBot.PayloadType
  const isMedia = type === "photo" || type === "document"

  return (
    <Stack spacing={2}>
      <AutocompleteElement
        name="type"
        label="Тип ответа"
        options={TYPE_OPTIONS}
        autocompleteProps={{
          getOptionLabel: (o) => o.label,
          disableClearable: true,
        }}
        matchId
      />
      <AutocompleteElement
        name="parseMode"
        label="Форматирование"
        options={PARSE_MODE_OPTIONS}
        autocompleteProps={{ getOptionLabel: (o) => o.label }}
        matchId
      />
      <TextFieldElement
        name="text"
        label={isMedia ? "Подпись" : "Текст сообщения"}
        multiline
        rows={6}
        fullWidth
        validation={{
          validate: (v: string) =>
            watch("type") !== "text" || !!v || "Укажите текст сообщения",
        }}
      />
      {isMedia && (
        <TextFieldElement
          name="media"
          label="Ссылка на файл (URL)"
          fullWidth
          required
          validation={{
            validate: (v: string) =>
              watch("type") === "text" ||
              isUrl(v) ||
              "Укажите корректную ссылку (URL) на файл",
          }}
        />
      )}
      {type === "document" && (
        <>
          <TextFieldElement name="title" label="Заголовок" fullWidth required />
          <AutocompleteElement
            name="mime"
            label="Тип документа"
            options={MIME_OPTIONS}
            autocompleteProps={{
              getOptionLabel: (o) => o.label,
              disableClearable: true,
            }}
            matchId
          />
        </>
      )}
      <UrlButtons
        // Границей типов управляет buttonsAdapter (юнит-тесты гарантируют
        // рантайм-форму), поэтому здесь допустимы `as any`.
        data={buttonsToInlineKeyboard(watch("buttons")) as any}
        onChange={(val) =>
          setValue("buttons", inlineKeyboardToButtons(val as any))
        }
      />
    </Stack>
  )
}
